import Head from "next/head";
import { useRouter } from "next/router";

import { useMemo } from "react";

import { twMerge } from "tailwind-merge";

import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons/faQuestionCircle";
import { faWrench } from "@fortawesome/free-solid-svg-icons/faWrench";

import Breadcrumb, { BreadcrumbItem } from "components/Breadcrumb";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import PaneLayout from "components/PaneLayout";
import useMediaQuery from "hooks/useMediaQuery";
import { emberAppBaseUrl } from "utils/envVariables";

const ToolPageLayout = ({
  children,
  bottomPane,
  breadcrumb,
  title,
  description,
  keywords,
  showDisclaimer = true,
  showFooter = true,
  shareable,
  customBreadcrumbElements,
  transparentBreadcrumb,
}: {
  children?: React.ReactNode;
  bottomPane?: React.ReactNode;
  breadcrumb?: BreadcrumbItem[];
  title?: string;
  description?: string;
  keywords?: string;
  showDisclaimer?: boolean;
  showFooter?: boolean;
  shareable?: boolean;
  customBreadcrumbElements?: React.ReactNode;
  transparentBreadcrumb?: boolean;
}) => {
  const router = useRouter();
  const { mobile } = useMediaQuery();
  const metaTitle = title ? `${title} | Smartkarma Tools` : "Smartkarma Tools";
  const metaDesc = description ? description : "Smartkarma Tools";
  const metaKeywords = keywords ? keywords : "Smartkarma Tools";

  const canonicalUrl =
    emberAppBaseUrl + (router.asPath === "/" ? "" : router.asPath);

  const footer = useMemo(() => {
    return (
      <div className="px-[10px] md:px-0">
        {showDisclaimer && (
          <div className="text-[13px]">
            <span className="font-bold">Disclaimer:</span> Smartkarma makes no
            assurance about the accuracy of the generated data and results. Data
            is indicative only and and should be independently checked for
            accuracy and confirmed before use.
          </div>
        )}
        <div className="flex justify-end my-[15px]">
          <a
            href="mailto:helpdesk@smartkarma.com"
            target="_blank"
            className="text-[13px] text-shady-gray cursor-pointer"
          >
            <FontAwesomeIcon icon={faQuestionCircle} /> Send your feedback
          </a>
        </div>
      </div>
    );
  }, [showDisclaimer]);

  const meta = useMemo(() => {
    return (
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={metaKeywords} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://assets.smartkarma.com/v3/img/smartkarma-research-reinvented-dark.png"
        />
        <meta property="og:image:width" content="200" />
        <meta property="og:image:height" content="200" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Head>
    );
  }, [canonicalUrl, metaDesc, metaKeywords, metaTitle]);

  if (mobile) {
    return (
      <>
        {meta}
        <div
          className={twMerge(
            "w-full",
            "bg-white rounded-md shadow",
            "p-[10px] pt-[65px]"
          )}
        >
          <Breadcrumb
            items={[
              {
                title: "Tools",
                icon: <FontAwesomeIcon icon={faWrench} />,
                href: `/tools`,
              },
              ...(breadcrumb || []),
            ]}
            shareable={shareable}
          />
          <div>{children}</div>
        </div>
        {bottomPane}
        {showFooter && footer}
      </>
    );
  }

  return (
    <>
      {meta}
      <PaneLayout
        hideLeft
        hideRight
        hideActionBar
        className="pl-[35px] pr-[50px] py-[25px]"
      >
        <div className="w-full">
          <div
            className={twMerge(
              transparentBreadcrumb ? "" : "bg-white rounded-md shadow",
              "p-[15px]"
            )}
          >
            <Breadcrumb
              items={[
                {
                  title: "Tools",
                  icon: <FontAwesomeIcon icon={faWrench} />,
                  href: `/tools`,
                },
                ...(breadcrumb || []),
              ]}
              shareable={shareable}
              customRightElements={customBreadcrumbElements}
            />
            <div>{children}</div>
          </div>
          {bottomPane}
          {showFooter && <div className="mt-[20px]">{footer}</div>}
        </div>
      </PaneLayout>
    </>
  );
};

export default ToolPageLayout;
